import type { TableMenuItem } from '@/components_deprecated'
import {
  Table as BaseTable,
  Button,
  SortableHeader,
  TableMenu,
} from '@/components_deprecated'
import { ActivitiesStatus } from '@/modules/activities/components/overview/ActivitiesStatusCell'
import { cleanRegexOperators } from '@/utils/forms'
import {
  useAdmin,
  useInfiniteScroll,
  useOnErrorAuto,
  usePermissions,
} from '@/utils/hooks'
import { DEFAULT_PAGE_SIZE } from '@/utils/pagination'
import { safeParsePhoneNumber } from '@/utils/parsing'
import { PERMISSIONS } from '@/utils/permissions'
import { useMutation, useQuery } from '@apollo/client'
import { Icon, usePrompt, useToast } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { differenceInDays, format, parseISO } from 'date-fns'
import React, { useCallback, useEffect } from 'react'
import { isMobile } from 'react-device-detect'
import { useNavigate } from 'react-router'
import styled, { css, useTheme } from 'styled-components'
import type {
  AllUsersQuery,
  AllUsersQueryVariables,
  SendNewUserRegistration,
  SendNewUserRegistrationVariables,
  UserNode,
} from '..'
import { UserActivationModal } from '../components'
import { SEND_NEW_USER_REGISTRATION_MUTATION } from '../mutations'
import { USERS_QUERY } from '../queries'
import { UserProfileLink } from '../UserProfile/UserProfileLink'
import BaseUserThumbOrInitials from '../UserThumbOrInitials'
import { getUserLabel } from '../util'
import { type Metrics } from '../AllUsers'

const Table = styled(BaseTable)`
  td.name {
    display: flex;
    align-items: center;
  }
`
const StatusWrapper = styled.span`
  display: flex;
  justify-content: space-between;
  padding-right: 1rem;
`

const StatusText = styled.span`
  display: flex;
  flex-direction: column;
`

const FetchStatusSpan = styled.span`
  display: flex;
`

interface UserThumbOrInitialsProps {
  strike: boolean
}
const UserThumbOrInitials = styled(
  BaseUserThumbOrInitials
)<UserThumbOrInitialsProps>`
  position: relative;
  margin-right: 12px;

  ${props =>
    props.strike &&
    css`
      &::after {
        content: '';
        box-sizing: content-box;
        position: absolute;
        z-index: 1;
        top: 50%;
        left: 0;

        transform: translateY(-50%) rotate(135deg);
        width: 100%;
        height: 3px;

        border: 1px solid white;
        border-radius: 999px;
        background: #a50a0a;
        pointer-events: none;
      }
    `};
`

interface UsersTableProps {
  query?: string
  sort?: string
  showDeactivated?: boolean
  showOnlyDeactivated?: boolean
  includeExpiredDocuments?: boolean
  documentCategorySelection?: string[]

  onSort?: (value: string) => void

  updateMetrics: (newMetrics: Partial<Metrics>) => void
}

export const UsersTable: React.FC<UsersTableProps> = ({
  query = '',
  sort = 'lastName',
  showDeactivated = false,
  showOnlyDeactivated = false,
  includeExpiredDocuments = false,
  documentCategorySelection = [],

  onSort = () => void 0,

  updateMetrics,
}) => {
  const translations = useTranslate({
    name: 'common.name',
    status: 'common.status',
    data: 'common.data',
    phone: 'common.phone',
    email: 'common.email',
    role: 'common.role',
    active: 'common.active',
    deactivated: 'common.deactivated',
    awaitsConfiguration: 'users.awaits-configuration',
    missingExternalId: 'users.missing-employee-number',
    id: 'common.id',
    sendNewInvitation: 'users.send-new-invitation',

    editUser: 'users.edit-user',
    activateUser: 'users.activate-user',
    deactivateUser: 'users.deactivate-user',

    userIsDeactivated: 'users.user-is-deactivated',
    noResults: 'common.no-results',

    toastUserActivated: ['users.toasts.user-activated', { name: '' }],
    toastUserDeactivated: ['users.toasts.user-deactivated', { name: '' }],
    errorGeneric: 'error.generic-server-error',

    newRegistrationLinkSend: 'users.new-registration-link-send',
  })

  const navigate = useNavigate()

  const theme = useTheme()
  const addToast = useToast()
  const onErrorAuto = useOnErrorAuto()

  const addPrompt = usePrompt()

  const {
    data,
    loading: queryLoading,
    fetchMore,
  } = useQuery<AllUsersQuery, AllUsersQueryVariables>(USERS_QUERY, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      first: DEFAULT_PAGE_SIZE,
      q: cleanRegexOperators(query),
      orderBy: sort,
      includeActive: showOnlyDeactivated ? false : undefined,
      includeInactive: showOnlyDeactivated ? true : false,
      includeExpiredDocuments: includeExpiredDocuments,
      onlyIncludeUserDocumentCategories: documentCategorySelection,
    },
    onError: onErrorAuto(),
  })

  const [sendNewUserRegistration, { loading: newUserRegistrationLoading }] =
    useMutation<SendNewUserRegistration, SendNewUserRegistrationVariables>(
      SEND_NEW_USER_REGISTRATION_MUTATION,
      {
        awaitRefetchQueries: true,
        refetchQueries: ['Users'],
        onCompleted(data) {
          if (typeof data === 'undefined') {
            addToast('error', translations.errorGeneric)
            return
          }

          addToast('success', translations.newRegistrationLinkSend)
        },
        onError: onErrorAuto(translations.errorGeneric),
      }
    )

  const users = data?.allUsers.edges.map(edge => edge.node) ?? []
  const hasNextPage = data?.allUsers.pageInfo.hasNextPage ?? false

  const admin = useAdmin()
  const { hasPermissions } = usePermissions()
  const showMenu = admin || hasPermissions(PERMISSIONS.users.change.user)
  const handleScrollBottom = useCallback(async () => {
    if (typeof data === 'undefined') return

    try {
      await fetchMore({
        variables: {
          first: DEFAULT_PAGE_SIZE,
          q: cleanRegexOperators(query),
          orderBy: sort,
          isActive: showDeactivated ? undefined : true,
          after: data.allUsers.pageInfo.endCursor,
        },
      })
    } catch (e) {
      if (e instanceof Error && e.name === 'Invariant Violation') return
      throw e
    }
  }, [data, fetchMore, query, showDeactivated, sort])

  useInfiniteScroll(handleScrollBottom, 200, !queryLoading && hasNextPage)

  function handleEditUser(user?: UserNode) {
    if (typeof user === 'undefined') return

    void navigate(`/users/${user.id}`, {
      state: { next: '/users', edit: true },
    })
  }

  async function handleToggleUserActivation(user?: UserNode) {
    if (typeof user === 'undefined') return

    await addPrompt<boolean>(resolve => (
      <UserActivationModal user={user} onResolve={resolve} />
    ))
  }

  function createMenuItems(user: UserNode) {
    const items: TableMenuItem<UserNode>[] = []

    items.push({
      label: translations.editUser,
      data: user,
      role: 'link',
      onClick: handleEditUser,
    })

    if (admin) {
      items.push({
        label: user.isActive
          ? translations.deactivateUser
          : translations.activateUser,
        data: user,
        onClick: handleToggleUserActivation,
      })
    }

    return items
  }

  function handleResendInvitation(user: UserNode) {
    void sendNewUserRegistration({
      variables: {
        userId: user.id,
      },
    })
  }

  function getStatusTextForUser(user: UserNode) {
    return user.hasRegistered
      ? user.isActive
        ? user.internalNumber
          ? translations.active
          : translations.missingExternalId
        : translations.deactivated
      : translations.awaitsConfiguration
  }

  /**
   * Whenever data is updated, call updateMetrics to inform parent component
   * about the new total user count and missing configuration count.
   */
  useEffect(() => {
    if (!data) return

    const totalCount = data.allUsers.totalCount
    const missingConfigCount = data.allUsers.missingConfigurationCount
    const activeUserCount = data.allUsers.activeUserCount

    updateMetrics({
      totalCount,
      missingConfigCount,
      activeUserCount,
    })
  }, [data, updateMetrics])

  return (
    <>
      <Table
        loading={queryLoading}
        loaderProps={{
          columns: !isMobile ? 6 : 1,
          rows: 10,
          expectedWidths: !isMobile ? [150, 130, 220, 150, 50, 150, 20] : [],
        }}
        noData={users.length === 0}
        noDataText={translations.noResults}
      >
        <thead>
          <tr>
            <SortableHeader
              baseValue="lastName"
              sortValue={sort}
              onSort={onSort}
              style={!isMobile ? { minWidth: 180 } : undefined}
            >
              {translations.name}
            </SortableHeader>

            {!isMobile && (
              <>
                <SortableHeader
                  baseValue="phoneNumber"
                  sortValue={sort}
                  onSort={onSort}
                  style={{ minWidth: 160 }}
                >
                  {translations.phone}
                </SortableHeader>

                <SortableHeader
                  baseValue="email"
                  sortValue={sort}
                  onSort={onSort}
                  style={{ minWidth: 220 }}
                >
                  {translations.email}
                </SortableHeader>

                <SortableHeader
                  baseValue="positionSortValue"
                  sortValue={sort}
                  onSort={onSort}
                  style={{ minWidth: 180 }}
                >
                  {translations.role}
                </SortableHeader>

                {admin && (
                  <SortableHeader
                    baseValue="internalNumber"
                    sortValue={sort}
                    onSort={onSort}
                    style={{ minWidth: 60 }}
                  >
                    {translations.id}
                  </SortableHeader>
                )}

                <SortableHeader
                  baseValue="statusSortValue"
                  sortValue={sort}
                  onSort={onSort}
                  style={{ minWidth: 180 }}
                >
                  {translations.status}
                </SortableHeader>
              </>
            )}

            {admin && !isMobile && <th>{translations.data}</th>}

            {isMobile && <th></th>}

            {(isMobile || showMenu) && <th></th>}
          </tr>
        </thead>

        <tbody>
          {users.map((user, userIndex) => {
            const phone = user.phoneNumber
              ? safeParsePhoneNumber(user.phoneNumber, 'international')
              : null

            const menuItems = createMenuItems(user)

            const deactivated = !user.isActive

            const statusLabel =
              admin && !user.hasRegistered && !user.hasLoginCode ? (
                <Button
                  fontSize="1rem"
                  padding="0.25rem!important"
                  height="auto"
                  disabled={newUserRegistrationLoading}
                  onClick={() => handleResendInvitation(user)}
                >
                  {translations.sendNewInvitation}
                </Button>
              ) : (
                getStatusTextForUser(user)
              )

            return (
              <tr key={user.id}>
                <td className="name">
                  <UserThumbOrInitials
                    user={user}
                    size={46}
                    fontWeight={600}
                    strike={deactivated}
                    title={
                      deactivated ? translations.userIsDeactivated : undefined
                    }
                  />
                  <UserProfileLink
                    user={user}
                    userNameTransform={getUserLabel}
                  />
                </td>

                {!isMobile && (
                  <>
                    <td>
                      {!phone ? (
                        '-'
                      ) : (
                        <a href={`tel:${user.phoneNumber}`}>{phone}</a>
                      )}
                    </td>

                    <td>
                      {!user.email ? (
                        '-'
                      ) : (
                        <a href={`mailto:${user.notificationEmail}`}>
                          {user.notificationEmail}
                        </a>
                      )}
                    </td>

                    <td>{user.userTypes.map(ut => ut.name).join(', ')}</td>

                    {admin && !isMobile && <td>{user.internalNumber}</td>}

                    <td>
                      <StatusWrapper>
                        <StatusText>{statusLabel}</StatusText>
                        {user.hasActiveDriverCard && (
                          <Icon icon="id-card" type="solid" color="primary" />
                        )}
                      </StatusWrapper>
                    </td>

                    {admin && !isMobile && (
                      <td>
                        {user.driverFetchInfo.map(driverFetchInfo => {
                          if (!driverFetchInfo.lastFetch)
                            return (
                              <React.Fragment
                                key={driverFetchInfo.id}
                              ></React.Fragment>
                            )

                          const fetchDate = parseISO(driverFetchInfo.lastFetch)

                          const fetchDifference = differenceInDays(
                            new Date(),
                            fetchDate
                          )

                          const color =
                            fetchDifference < 7
                              ? theme.colors.green
                              : fetchDifference < 10
                              ? theme.colors.orange
                              : theme.colors.red

                          const tooltipProps =
                            userIndex === users.length - 1
                              ? { top: '-200%' }
                              : undefined

                          const endpoint = driverFetchInfo.integrationEndpoint
                            .toLowerCase()
                            .includes('volvo')
                            ? 'Volvo'
                            : driverFetchInfo.integrationEndpoint
                                .toLowerCase()
                                .includes('scania')
                            ? 'Scania'
                            : driverFetchInfo.integrationEndpoint
                                .toLowerCase()
                                .includes('man')
                            ? 'MAN'
                            : 'FleetBoard'

                          return (
                            <FetchStatusSpan key={driverFetchInfo.id}>
                              <ActivitiesStatus
                                dataStatusColor={color}
                                tooltip={format(fetchDate, 'HH:mm dd.MM.yyyy')}
                                tooltipProps={tooltipProps}
                              />
                              {endpoint}
                            </FetchStatusSpan>
                          )
                        })}
                      </td>
                    )}

                    {showMenu && <TableMenu items={menuItems} />}
                  </>
                )}

                {isMobile && (
                  <>
                    <td width="1px">
                      {!!user.email && (
                        <a href={`mailto:${user.email}`}>
                          <Icon icon="envelope" type="solid" color="primary" />
                        </a>
                      )}
                    </td>

                    <td width="1px">
                      {!!phone && (
                        <a href={`tel:${user.phoneNumber}`}>
                          <Icon icon="phone" type="solid" color="primary" />
                        </a>
                      )}
                    </td>
                  </>
                )}
              </tr>
            )
          })}
        </tbody>
      </Table>
    </>
  )
}
